import { type FC, useLayoutEffect, useRef, useState } from 'react'

import { Heading, type HeadingProps } from '@foxino/components-web'

const fontSizes: HeadingProps['size'][] = ['2xl', 'xl', 'lg', 'md', 'sm', 'xs', '2xs']

type Props = {
  defaultFontSize?: HeadingProps['size']
  parentWidth: number
} & HeadingProps

/**
 * Self-sizing heading component that adjusts its font size based on the available width.
 * @param param0
 * @returns
 */
export const SelfSizingHeading: FC<Props> = ({ defaultFontSize = '2xl', children, parentWidth, ...otherProps }) => {
  const [fontSize, setFontSize] = useState<HeadingProps['size']>(defaultFontSize)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [isAdjusting, setIsAdjusting] = useState(false)

  useLayoutEffect(() => {
    if (!headingRef.current || !parentWidth || isAdjusting) return

    const currentWidth = headingRef.current.scrollWidth
    const currentHeight = headingRef.current.scrollHeight

    // If current text fits, we're done
    if (currentWidth <= parentWidth && currentHeight <= parentWidth) {
      return
    }

    // Find current size index
    const currentIndex = fontSizes.indexOf(fontSize)
    const nextIndex = currentIndex + 1

    // If we can go smaller, do it
    if (nextIndex < fontSizes.length) {
      setIsAdjusting(true)
      setFontSize(fontSizes[nextIndex])

      // Reset adjusting flag after state update
      requestAnimationFrame(() => {
        setIsAdjusting(false)
      })
    }
  }, [parentWidth, children, fontSize, isAdjusting])

  // Reset to default size when content or parentWidth changes significantly
  useLayoutEffect(() => {
    if (!isAdjusting) {
      setFontSize(defaultFontSize)
    }
  }, [parentWidth, children, defaultFontSize])

  return (
    <Heading ref={headingRef} {...otherProps} size={fontSize} className="w-full">
      {children}
    </Heading>
  )
}
